import { AlertTriangle, History, PackagePlus, Pencil, Plus, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useHardConfirm } from "../../components/HardConfirmDialog";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../../components/ui/Dialog";
import {
  ApiError,
  isOfflineApiError,
  providerApi,
  type FoodItemRecord,
  type FoodStockAdjustmentKind,
  type FoodStockMovementRecord,
} from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { useProviderOffline } from "../../lib/provider-offline";
import { useAuth } from "../../lib/auth";
import { formatEgp } from "../provider-orders/order-status";
import { useVenueMenu } from "../provider-orders/useVenueMenu";
import { useProviderWorkspace } from "../provider-workspace/ProviderWorkspaceContext";
import { resolveProviderMembershipRole } from "../provider-workspace/provider-workspace-helpers";
import { MenuItemDialog, MenuThumb } from "./MenuItemDialog";

type Filter = "all" | "attention" | "hidden";

function errorText(error: unknown, fallback: string) {
  return error instanceof ApiError && error.status !== 0 ? error.detail : fallback;
}

export function MenuStockSection() {
  const { t, language } = useAppPreferences();
  const { user } = useAuth();
  const { selectedVenue, summary } = useProviderWorkspace();
  const { isOnline, queueMutation } = useProviderOffline();
  const { confirm, dialog: confirmDialog } = useHardConfirm();
  const venueId = selectedVenue?.id ?? null;
  const menu = useVenueMenu(venueId);
  const role = resolveProviderMembershipRole(summary, user);
  const canEditMenu = role === "owner";
  const canCountStock = role === "owner" || role === "manager";

  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<FoodItemRecord | "new" | null>(null);
  const [stockTarget, setStockTarget] = useState<FoodItemRecord | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [notice, setNotice] = useState<{ tone: "success" | "danger"; text: string } | null>(null);

  const attention = menu.items.filter((item) => item.sold_out || item.low_stock);
  const soldOutCount = menu.items.filter((item) => item.sold_out).length;
  const lowCount = menu.items.filter((item) => item.low_stock).length;

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return menu.items.filter((item) => {
      if (filter === "attention" && !(item.sold_out || item.low_stock)) return false;
      if (filter === "hidden" && item.is_available) return false;
      return !needle || item.name.toLowerCase().includes(needle) || item.category.toLowerCase().includes(needle);
    });
  }, [filter, menu.items, query]);

  const grouped = useMemo(() => {
    const groups = new Map<string, FoodItemRecord[]>();
    for (const item of visible) {
      const list = groups.get(item.category) ?? [];
      list.push(item);
      groups.set(item.category, list);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [visible]);

  const toggleOnMenu = async (item: FoodItemRecord) => {
    if (!venueId) return;
    const payload = { is_available: !item.is_available };
    setNotice(null);
    try {
      menu.replaceItem(await providerApi.updateFoodItem(venueId, item.id, payload));
    } catch (error) {
      if (isOfflineApiError(error)) {
        menu.replaceItem({ ...item, ...payload, updated_at: new Date().toISOString() });
        queueMutation({
          method: "PUT",
          path: `/api/v1/provider/venues/${venueId}/food/${item.id}`,
          body: payload,
          label: `Toggle availability for ${item.name}`,
        });
        setNotice({ tone: "success", text: t("Saved offline. It will sync when you're back online.") });
      } else {
        setNotice({ tone: "danger", text: errorText(error, t("Could not update item.")) });
      }
    }
  };

  const remove = async (item: FoodItemRecord) => {
    if (!venueId || !selectedVenue) return;
    const confirmed = await confirm({
      confirmationValue: item.name.trim() || "DELETE",
      confirmButtonLabel: t("Delete item"),
      confirmFieldLabel: t("Type {value} to permanently delete this item", { value: item.name.trim() || "DELETE" }),
      consequence: t("Items that were ever ordered can't be deleted; take them off the menu instead."),
      description: t("This permanently removes {name} from {venueName}.", { name: item.name, venueName: selectedVenue.name }),
      title: t("Delete {name}?", { name: item.name }),
    });
    if (!confirmed) return;
    try {
      await providerApi.deleteFoodItem(venueId, item.id);
      menu.persist(menu.items.filter((entry) => entry.id !== item.id));
      setEditing(null);
      setNotice({ tone: "success", text: t("{name} deleted.", { name: item.name }) });
    } catch (error) {
      setNotice({ tone: "danger", text: errorText(error, t("Could not delete item.")) });
    }
  };

  if (!selectedVenue) {
    return <div className="ops-empty py-10">{t("Pick a venue to manage its menu.")}</div>;
  }

  return (
    <div className="grid gap-4">
      {confirmDialog}
      <section className="ops-surface grid gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="ops-kicker">{selectedVenue.name}</p>
            <h1 className="ops-page-title">{t("Menu & stock")}</h1>
            <p className="ops-copy mt-1 text-sm">
              {t("What guests can order, and how many you have left. Stock goes down with every order and back up if an order is cancelled.")}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="ops-button" data-variant="secondary" onClick={() => setHistoryOpen(true)} type="button">
              <History size={16} aria-hidden="true" /> {t("Stock history")}
            </button>
            {canEditMenu ? (
              <button className="ops-button" data-variant="primary" onClick={() => setEditing("new")} type="button">
                <Plus size={16} aria-hidden="true" /> {t("Add item")}
              </button>
            ) : null}
          </div>
        </div>

        {attention.length > 0 ? (
          <div className="ops-banner" data-tone="warning" role="status">
            <AlertTriangle aria-hidden="true" size={16} />
            <span className="min-w-0 flex-1">
              {[
                soldOutCount > 0 ? t(soldOutCount === 1 ? "{count} item sold out" : "{count} items sold out", { count: soldOutCount }) : null,
                lowCount > 0 ? t(lowCount === 1 ? "{count} item running low" : "{count} items running low", { count: lowCount }) : null,
              ].filter(Boolean).join(" · ")}
              {": "}
              {attention.map((item) => item.name).join(", ")}
            </span>
            <button className="ops-button" data-size="sm" data-variant="secondary" onClick={() => setFilter("attention")} type="button">
              {t("Show")}
            </button>
          </div>
        ) : null}

        {notice ? (
          <div className="ops-banner" data-tone={notice.tone} role={notice.tone === "danger" ? "alert" : "status"}>
            <span className="min-w-0 flex-1">{notice.text}</span>
            <button className="ops-button" data-size="sm" data-variant="secondary" onClick={() => setNotice(null)} type="button">{t("Dismiss")}</button>
          </div>
        ) : null}
        {menu.error ? (
          <div className="ops-banner" data-tone="danger" role="alert">
            <span className="min-w-0 flex-1">{menu.error}</span>
            <button className="ops-button" data-size="sm" data-variant="secondary" onClick={() => void menu.reload()} type="button">{t("Retry")}</button>
          </div>
        ) : null}
        {menu.isCached ? <div className="ops-banner" data-tone="warning">{t("Offline: showing the last saved menu. Stock numbers may be out of date.")}</div> : null}

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1.5" role="group" aria-label={t("Show")}>
            {([
              ["all", t("All items ({count})", { count: menu.items.length })],
              ["attention", t("Needs restock ({count})", { count: attention.length })],
              ["hidden", t("Off the menu ({count})", { count: menu.items.filter((item) => !item.is_available).length })],
            ] as Array<[Filter, string]>).map(([value, label]) => (
              <button
                aria-pressed={filter === value}
                className="ops-button"
                data-size="sm"
                data-variant={filter === value ? "primary" : "secondary"}
                key={value}
                onClick={() => setFilter(value)}
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
          <label className="order-composer-search ms-auto min-w-[12rem] flex-1 sm:max-w-xs">
            <Search aria-hidden="true" size={16} />
            <span className="sr-only">{t("Search items")}</span>
            <input className="ops-input" onChange={(event) => setQuery(event.target.value)} placeholder={t("Search items")} type="search" value={query} />
          </label>
        </div>

        {menu.isLoading && menu.items.length === 0 ? <div className="ops-empty py-6">{t("Loading menu…")}</div> : null}
        {!menu.isLoading && menu.items.length === 0 && !menu.error ? (
          <div className="ops-empty grid gap-2 py-8">
            <p>{t("No menu items yet.")}</p>
            <p className="ops-copy text-xs">{t("Add the food and drinks you sell. Turn on stock tracking for anything you want to count, like cans and snacks.")}</p>
          </div>
        ) : null}
        {menu.items.length > 0 && visible.length === 0 ? <p className="ops-copy text-sm">{t("No items match.")}</p> : null}

        {grouped.map(([category, items]) => (
          <div className="grid gap-2" key={category}>
            <p className="ops-kicker menu-category-label">{t(category)}</p>
            <ul className="menu-stock-list">
              {items.map((item) => (
                <li className="menu-stock-row" data-off={!item.is_available ? "true" : undefined} key={item.id} data-testid="menu-stock-row">
                  <MenuThumb src={item.image_url} />
                  <div className="min-w-0">
                    <p className="break-words text-sm font-semibold">{item.name}</p>
                    <p className="ops-copy text-xs">{formatEgp(item.price, language)}</p>
                  </div>
                  <div className="menu-stock-level">
                    {item.track_stock ? (
                      <>
                        <span className="menu-stock-count" data-tone={item.sold_out ? "danger" : item.low_stock ? "warning" : undefined}>
                          {item.stock_quantity}
                        </span>
                        <span className="ops-copy text-xs">
                          {item.sold_out ? t("Sold out") : item.low_stock ? t("Running low") : t("in stock")}
                        </span>
                      </>
                    ) : (
                      <span className="ops-copy text-xs">{t("Stock not counted")}</span>
                    )}
                  </div>
                  <label className="menu-stock-switch" title={t("Guests can order it")}>
                    <input
                      checked={item.is_available}
                      disabled={!canEditMenu}
                      onChange={() => void toggleOnMenu(item)}
                      type="checkbox"
                    />
                    <span>{item.is_available ? t("On the menu") : t("Hidden")}</span>
                  </label>
                  <div className="menu-stock-actions">
                    {canCountStock || item.track_stock ? (
                      <button
                        className="ops-button"
                        data-size="sm"
                        data-variant={item.sold_out || item.low_stock ? "primary" : "secondary"}
                        disabled={!isOnline}
                        onClick={() => setStockTarget(item)}
                        title={!isOnline ? t("Stock changes need a connection.") : undefined}
                        type="button"
                      >
                        <PackagePlus size={14} aria-hidden="true" /> {item.track_stock ? t("Update stock") : t("Track stock")}
                      </button>
                    ) : null}
                    {canEditMenu ? (
                      <button
                        aria-label={t("Edit {name}", { name: item.name })}
                        className="ops-button"
                        data-size="sm"
                        data-variant="secondary"
                        onClick={() => setEditing(item)}
                        type="button"
                      >
                        <Pencil size={14} aria-hidden="true" /> <span className="max-sm:sr-only">{t("Edit")}</span>
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {!canEditMenu ? (
          <p className="ops-copy text-xs">
            {canCountStock
              ? t("Only the owner can add items or change prices. You can update stock.")
              : t("Only the owner can change the menu. Updating stock needs a manager's approval.")}
          </p>
        ) : null}
      </section>

      {editing && venueId ? (
        <MenuItemDialog
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onDelete={editing === "new" ? undefined : () => void remove(editing)}
          onSaved={(item, offline) => {
            menu.replaceItem(item);
            setEditing(null);
            setNotice({
              tone: "success",
              text: offline ? t("Saved offline. It will sync when you're back online.") : t("{name} saved.", { name: item.name }),
            });
          }}
          venueId={venueId}
        />
      ) : null}

      {stockTarget && venueId ? (
        <StockDialog
          canEditThreshold={canEditMenu}
          item={stockTarget}
          onClose={() => setStockTarget(null)}
          onSaved={(item, message) => {
            menu.replaceItem(item);
            setStockTarget(null);
            setNotice({ tone: "success", text: message });
          }}
          venueId={venueId}
        />
      ) : null}

      {historyOpen && venueId ? <StockHistoryDialog onClose={() => setHistoryOpen(false)} venueId={venueId} /> : null}
    </div>
  );
}

function StockDialog({
  canEditThreshold,
  item,
  onClose,
  onSaved,
  venueId,
}: {
  canEditThreshold: boolean;
  item: FoodItemRecord;
  onClose: () => void;
  onSaved: (item: FoodItemRecord, message: string) => void;
  venueId: string;
}) {
  const { t } = useAppPreferences();
  const tracked = Boolean(item.track_stock);
  const [kind, setKind] = useState<FoodStockAdjustmentKind>(tracked ? "restock" : "count");
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");
  const [threshold, setThreshold] = useState(item.low_stock_threshold != null ? String(item.low_stock_threshold) : "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = item.stock_quantity ?? 0;
  const amount = Number.parseInt(quantity, 10);
  const valid = Number.isFinite(amount) && amount >= 0 && (kind === "count" || amount > 0);
  const after = !valid ? null : kind === "restock" ? current + amount : kind === "waste" ? current - amount : amount;

  const submit = async () => {
    if (!valid) {
      setError(kind === "count" ? t("Enter how many are on the shelf.") : t("Enter a number above 0."));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let updated = await providerApi.adjustFoodStock(venueId, item.id, { kind, quantity: amount, note: note.trim() || null });
      const parsedThreshold = threshold.trim() === "" ? null : Number.parseInt(threshold, 10);
      if (canEditThreshold && parsedThreshold !== (item.low_stock_threshold ?? null)) {
        try {
          updated = await providerApi.updateFoodItem(venueId, item.id, parsedThreshold === null
            ? { clear_low_stock_threshold: true }
            : { low_stock_threshold: parsedThreshold });
        } catch {
          // The stock change is saved; a failed alert-level update should not undo it.
        }
      }
      onSaved(updated, t("{name}: {count} in stock.", { name: item.name, count: updated.stock_quantity ?? 0 }));
    } catch (requestError) {
      setError(errorText(requestError, t("Could not update stock. Check your connection and try again.")));
    } finally {
      setSaving(false);
    }
  };

  const kinds: Array<{ value: FoodStockAdjustmentKind; label: string; help: string }> = [
    { value: "restock", label: t("Add delivery"), help: t("You received more. Enter how many arrived.") },
    { value: "count", label: t("Set count"), help: t("You counted the shelf. Enter the exact number you have.") },
    { value: "waste", label: t("Remove"), help: t("Spoiled, broken or used by staff. Enter how many to take off.") },
  ];

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogTitle>{tracked ? t("Update stock: {name}", { name: item.name }) : t("Start counting {name}", { name: item.name })}</DialogTitle>
        <DialogDescription>
          {tracked
            ? t("{count} in stock now.", { count: current })
            : t("Enter how many you have. From then on, each order takes from this number and the item hides itself when it runs out.")}
        </DialogDescription>
        <form className="grid gap-3" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
          {tracked ? (
            <div className="grid gap-1.5" role="radiogroup" aria-label={t("What happened?")}>
              {kinds.map((entry) => (
                <label className="stock-kind" data-selected={kind === entry.value ? "true" : undefined} key={entry.value}>
                  <input checked={kind === entry.value} name="stock-kind" onChange={() => setKind(entry.value)} type="radio" value={entry.value} />
                  <span>
                    <span className="block text-sm font-semibold">{entry.label}</span>
                    <span className="ops-copy block text-xs">{entry.help}</span>
                  </span>
                </label>
              ))}
            </div>
          ) : null}
          <label className="grid gap-1">
            <span className="ops-label">{kind === "count" ? t("How many are there now?") : kind === "restock" ? t("How many arrived?") : t("How many to remove?")}</span>
            <input autoFocus className="ops-input" inputMode="numeric" min={0} onChange={(event) => setQuantity(event.target.value)} type="number" value={quantity} />
          </label>
          {after !== null ? (
            <p className="ops-copy text-sm" data-tone={after < 0 ? "danger" : undefined}>
              {after < 0 ? t("You only have {count}.", { count: current }) : t("New stock: {count}", { count: after })}
            </p>
          ) : null}
          {canEditThreshold ? (
            <label className="grid gap-1">
              <span className="ops-label">{t("Warn me when it gets down to (optional)")}</span>
              <input className="ops-input" inputMode="numeric" min={0} onChange={(event) => setThreshold(event.target.value)} placeholder={t("e.g. 5")} type="number" value={threshold} />
            </label>
          ) : null}
          <label className="grid gap-1">
            <span className="ops-label">{t("Note (optional)")}</span>
            <input className="ops-input" maxLength={255} onChange={(event) => setNote(event.target.value)} placeholder={t("Supplier, invoice number, reason…")} value={note} />
          </label>
          {error ? <div className="ops-banner" data-tone="danger" role="alert">{error}</div> : null}
          <div className="flex flex-wrap justify-end gap-2">
            <button className="ops-button" data-variant="secondary" onClick={onClose} type="button">{t("Cancel")}</button>
            <button className="ops-button" data-variant="primary" disabled={saving || (after !== null && after < 0)} type="submit">
              {saving ? t("Saving…") : t("Save stock")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StockHistoryDialog({ onClose, venueId }: { onClose: () => void; venueId: string }) {
  const { t } = useAppPreferences();
  const [movements, setMovements] = useState<FoodStockMovementRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    providerApi.listFoodStockMovements(venueId, { limit: 100 })
      .then((response) => { if (!cancelled) setMovements(response.movements); })
      .catch((requestError) => { if (!cancelled) setError(errorText(requestError, t("Could not load stock history."))); });
    return () => { cancelled = true; };
  }, [t, venueId]);

  const reasonLabel = (reason: FoodStockMovementRecord["reason"]) => {
    switch (reason) {
      case "restock": return t("Delivery");
      case "count": return t("Counted");
      case "waste": return t("Removed");
      case "order": return t("Ordered");
      case "order_cancelled": return t("Order cancelled");
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogTitle>{t("Stock history")}</DialogTitle>
        <DialogDescription>{t("Every change to counted items, newest first.")}</DialogDescription>
        {error ? <div className="ops-banner" data-tone="danger" role="alert">{error}</div> : null}
        {movements === null && !error ? <div className="ops-empty py-4">{t("Loading…")}</div> : null}
        {movements && movements.length === 0 ? <p className="ops-copy text-sm">{t("No stock changes yet.")}</p> : null}
        {movements && movements.length > 0 ? (
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="ops-table-dense w-full text-sm">
              <thead>
                <tr>
                  <th className="text-start">{t("When")}</th>
                  <th className="text-start">{t("Item")}</th>
                  <th className="text-start">{t("What")}</th>
                  <th className="text-end">{t("Change")}</th>
                  <th className="text-end">{t("Left")}</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((movement) => (
                  <tr key={movement.id}>
                    <td className="whitespace-nowrap">{new Date(movement.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</td>
                    <td>{movement.item_name}</td>
                    <td>
                      {reasonLabel(movement.reason)}
                      {movement.note || movement.actor_name ? (
                        <span className="ops-copy block text-xs">{[movement.note, movement.actor_name].filter(Boolean).join(" · ")}</span>
                      ) : null}
                    </td>
                    <td className="text-end tabular-nums" data-tone={movement.change < 0 ? "danger" : "success"}>{movement.change > 0 ? `+${movement.change}` : movement.change}</td>
                    <td className="text-end tabular-nums">{movement.balance_after}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
